import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  HStack,
  VStack,
  Box,
  Text,
  Input,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  useToast,
} from '@chakra-ui/react';
import { useUserLists } from './useUserLists';

const sameId = (a, b) =>
  a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();

/**
 * User list CRUD. Top: Create | Manage toggles tinted panels.
 * Bottom: selectable list rows — pick one, open Manage for members/rename/delete.
 *
 * Props:
 *   isOpen   bool
 *   onClose  () => void
 *   userPool Array<{ id, name, email }>
 */
export function UserListManagerModal({ isOpen, onClose, userPool }) {
  const {
    lists,
    loading,
    createList,
    renameList,
    deleteList,
    getMembers,
    addMembers,
    removeMembers,
  } = useUserLists();
  const toast = useToast();

  const [newName, setNewName] = useState('');
  const [activePanel, setActivePanel] = useState(null); // 'create' | 'manage'
  const [selectedListId, setSelectedListId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [editingMembersOf, setEditingMembersOf] = useState(null);
  const [editingMemberIds, setEditingMemberIds] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setNewName('');
      setActivePanel(null);
      setSelectedListId(null);
      setRenamingId(null);
      setEditingMembersOf(null);
      setMemberSearch('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (activePanel === 'manage' && selectedListId) {
      setEditingMembersOf(selectedListId);
    } else {
      setEditingMembersOf(null);
    }
  }, [activePanel, selectedListId]);

  useEffect(() => {
    if (!editingMembersOf) {
      setEditingMemberIds([]);
      return undefined;
    }
    let cancelled = false;
    getMembers(editingMembersOf).then((ids) => {
      if (!cancelled) setEditingMemberIds(ids);
    });
    return () => {
      cancelled = true;
    };
  }, [editingMembersOf, getMembers]);

  const selectedList =
    lists.find((l) => sameId(l.id, selectedListId)) || null;

  const onCreate = async () => {
    try {
      await createList(newName);
      setNewName('');
      setActivePanel(null);
      toast({ title: 'List created', status: 'success', duration: 2000 });
    } catch (e) {
      toast({
        title: 'Could not create list',
        description: String(e.message || e),
        status: 'error',
        duration: 4000,
      });
    }
  };

  const onSaveRename = async () => {
    try {
      await renameList(renamingId, renameValue);
      setRenamingId(null);
      toast({ title: 'Renamed', status: 'success', duration: 2000 });
    } catch (e) {
      toast({
        title: 'Rename failed',
        description: String(e.message || e),
        status: 'error',
        duration: 4000,
      });
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this list? This cannot be undone.')) return;
    try {
      await deleteList(id);
      setSelectedListId(null);
      setActivePanel(null);
      toast({ title: 'List deleted', status: 'success', duration: 2000 });
    } catch (e) {
      toast({
        title: 'Delete failed',
        description: String(e.message || e),
        status: 'error',
        duration: 4000,
      });
    }
  };

  const onAddMember = async (userId) => {
    if (!editingMembersOf) return;
    await addMembers(editingMembersOf, [userId]);
    setEditingMemberIds((prev) => Array.from(new Set([...prev, userId])));
  };

  const onRemoveMember = async (userId) => {
    if (!editingMembersOf) return;
    await removeMembers(editingMembersOf, [userId]);
    setEditingMemberIds((prev) => prev.filter((id) => !sameId(id, userId)));
  };

  const candidateUsers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return (userPool || []).filter((u) => {
      if (editingMemberIds.some((id) => sameId(id, u.id))) return false;
      if (!q) return true;
      return (
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    });
  }, [userPool, editingMemberIds, memberSearch]);

  const memberDisplay = useMemo(() => {
    return editingMemberIds.map((id) => {
      const u = (userPool || []).find((x) => sameId(x.id, id));
      return u || { id, name: 'Unknown user', email: id };
    });
  }, [editingMemberIds, userPool]);

  const toggleCreate = () =>
    setActivePanel((p) => (p === 'create' ? null : 'create'));

  const toggleManage = () => {
    if (!selectedListId) {
      toast({
        title: 'Pick a list first',
        description: 'Select a list below, then tap Manage.',
        status: 'info',
        duration: 2500,
      });
      return;
    }
    setActivePanel((p) => (p === 'manage' ? null : 'manage'));
  };

  const openRenameForSelected = () => {
    if (!selectedList) return;
    setRenamingId(selectedList.id);
    setRenameValue(selectedList.name);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" shadow="xl" overflow="hidden">
        <ModalHeader
          pb={2}
          bg="linear-gradient(135deg, #ebf8ff 0%, #e6fffa 100%)"
          borderBottomWidth="1px"
          borderColor="blue.100"
        >
          <Text fontSize="lg" fontWeight="semibold" color="blue.900">
            Saved lists
          </Text>
          <Text fontSize="xs" fontWeight="normal" color="blue.700" mt={0.5}>
            Create a new group or manage members of one you&apos;ve picked.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={5} pb={4}>
          <HStack spacing={3} mb={4}>
            <Button
              flex={1}
              size="md"
              colorScheme="teal"
              variant={activePanel === 'create' ? 'solid' : 'outline'}
              onClick={toggleCreate}
            >
              Create
            </Button>
            <Button
              flex={1}
              size="md"
              colorScheme="blue"
              variant={activePanel === 'manage' ? 'solid' : 'outline'}
              onClick={() => {
                toggleManage();
              }}
            >
              Manage
            </Button>
          </HStack>

          {activePanel === 'create' && (
            <Box
              bg="teal.50"
              borderWidth={1}
              borderColor="teal.200"
              borderRadius="lg"
              p={4}
              mb={4}
            >
              <Text fontSize="sm" fontWeight="semibold" color="teal.800" mb={2}>
                New list
              </Text>
              <HStack spacing={2}>
                <Input
                  placeholder="Enter list name…"
                  value={newName}
                  bg="white"
                  borderColor="teal.200"
                  _focus={{ borderColor: 'teal.400', boxShadow: 'outline' }}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newName.trim()) onCreate();
                  }}
                />
                <Button
                  colorScheme="teal"
                  isDisabled={!newName.trim()}
                  onClick={onCreate}
                >
                  Create list
                </Button>
              </HStack>
            </Box>
          )}

          {activePanel === 'manage' && selectedListId && selectedList && (
            <Box
              bg="blue.50"
              borderWidth={1}
              borderColor="blue.200"
              borderRadius="lg"
              p={4}
              mb={4}
            >
              <VStack align="stretch" spacing={3}>
                {renamingId === selectedList.id ? (
                  <HStack>
                    <Input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      bg="white"
                      autoFocus
                    />
                    <Button size="sm" colorScheme="blue" onClick={onSaveRename}>
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRenamingId(null)}
                    >
                      Cancel
                    </Button>
                  </HStack>
                ) : (
                  <HStack justify="space-between" align="flex-start">
                    <Box>
                      <Text fontWeight="bold" color="blue.900">
                        {selectedList.name}
                      </Text>
                      <Text fontSize="xs" color="blue.700">
                        {selectedList.member_count}{' '}
                        member
                        {selectedList.member_count === 1 ? '' : 's'}
                      </Text>
                    </Box>
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        aria-label={`Rename ${selectedList.name}`}
                        onClick={openRenameForSelected}
                      >
                        Rename
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        onClick={() => onDelete(selectedList.id)}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </HStack>
                )}

                <Divider borderColor="blue.100" />

                <Text fontSize="xs" fontWeight="semibold" color="blue.800">
                  Members
                </Text>
                <HStack flexWrap="wrap" spacing={2} gap={2}>
                  {memberDisplay.length === 0 ? (
                    <Text fontSize="sm" color="blue.700">
                      No members yet — add people below.
                    </Text>
                  ) : (
                    memberDisplay.map((m) => (
                      <Tag
                        key={m.id}
                        size="sm"
                        colorScheme="blue"
                        variant="subtle"
                        borderRadius="full"
                      >
                        <TagLabel>{m.name || m.email || m.id}</TagLabel>
                        <TagCloseButton onClick={() => onRemoveMember(m.id)} />
                      </Tag>
                    ))
                  )}
                </HStack>
                <Input
                  placeholder="Search people to add…"
                  bg="white"
                  size="sm"
                  borderColor="blue.100"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />
                <Box
                  maxH="180px"
                  overflowY="auto"
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                >
                  {candidateUsers.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" p={3}>
                      Nobody matches search.
                    </Text>
                  ) : (
                    candidateUsers.slice(0, 50).map((u) => (
                      <HStack
                        key={u.id}
                        px={3}
                        py={2}
                        borderBottomWidth="1px"
                        borderColor="gray.50"
                        _hover={{ bg: 'blue.50' }}
                      >
                        <Box flex="1" minW={0}>
                          <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                            {u.name || u.email || u.id}
                          </Text>
                          {u.email && (
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                              {u.email}
                            </Text>
                          )}
                        </Box>
                        <Button size="xs" colorScheme="blue" onClick={() => onAddMember(u.id)}>
                          Add
                        </Button>
                      </HStack>
                    ))
                  )}
                </Box>
              </VStack>
            </Box>
          )}

          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
            Your lists
          </Text>
          {loading ? (
            <Text color="gray.500" fontSize="sm">
              Loading…
            </Text>
          ) : lists.length === 0 ? (
            <Box
              borderWidth="1px"
              borderRadius="lg"
              borderStyle="dashed"
              borderColor="gray.200"
              bg="gray.50"
              py={8}
              textAlign="center"
            >
              <Text color="gray.500" fontSize="sm">
                No lists yet. Tap <Text as="span" fontWeight="semibold" color="teal.700">Create</Text> above.
              </Text>
            </Box>
          ) : (
            <VStack align="stretch" spacing={2} maxH="280px" overflowY="auto">
              {lists.map((l) => {
                const isSel = selectedListId !== null && sameId(selectedListId, l.id);
                return (
                  <Box
                    key={l.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedListId(l.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedListId(l.id);
                      }
                    }}
                    cursor="pointer"
                    px={4}
                    py={3}
                    borderRadius="lg"
                    transition="border-color 0.15s, box-shadow 0.15s, background 0.15s"
                    bg={isSel ? 'blue.50' : 'white'}
                    borderWidth="2px"
                    borderColor={isSel ? 'blue.400' : 'gray.100'}
                    boxShadow={isSel ? 'sm' : 'none'}
                    _hover={{
                      borderColor: 'blue.200',
                      bg: isSel ? 'blue.50' : 'gray.50',
                    }}
                  >
                    <HStack justify="space-between" align="flex-start">
                      <Box minW={0}>
                        <Text fontWeight="semibold" color="gray.800" noOfLines={1}>
                          {l.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                          {l.member_count} member{l.member_count === 1 ? '' : 's'}
                        </Text>
                      </Box>
                      {isSel && (
                        <Text fontSize="2xs" color="blue.600" fontWeight="bold" letterSpacing="wider">
                          SELECTED
                        </Text>
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor="gray.100" pt={4}>
          <Button colorScheme="blue" variant="outline" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
